import React, { FC, useState } from 'react'
import { ConnectedProps, connect, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useManageEatable } from './useManageEatable'

import { DatePicker, FormInputFoodAmount, FormInputSelect, Mode } from 'Components'
import { CardRecipe } from 'Components/CardRecipe/CardRecipe'
import { deleteButton, editButton, favoriteButton } from 'Components/IconButton/iconButtons'
import {
  SelectItemKeyExtractor,
  SelectItemRenderer,
  SelectItemType,
  defaultKeyExtractor
} from 'Components/common/SelectPickerProps'
import { routes } from 'Config'
import { Amount, FavoritesMapping, MealCategory, Recipe, allMealCategories } from 'Models'
import FavoriteCardButtons from 'Pages/Favorites/FavoriteCardButtons'
import { favoriteSelector } from 'ReduxStore/favorites/favorites'
import { useAddOrRemoveFavorite } from 'ReduxStore/favorites/useAddOrRemoveFavorite'
import { useAppSelector } from 'ReduxStore/hooks'
import { setJournalDateAction } from 'ReduxStore/journalDay'
import { postOrPatchJournalEatableRecordAction } from 'ReduxStore/journalEatableRecords'
import { hideUserRecipeAction } from 'ReduxStore/userRecipes'
import { Formatter, Utils } from 'Utils'

const mapDispatchToProps = {
  hideItem: hideUserRecipeAction
}

const connector = connect(null, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type RecipeCardContainerProps = PropsFromRedux & {
  recipe: Recipe
  onClick: () => void
}

const RecipeCardContainer: FC<RecipeCardContainerProps> = ({ recipe, onClick, hideItem }: RecipeCardContainerProps) => {
  const { id: itemId } = recipe

  const favoritesRecipe = useAppSelector(favoriteSelector(FavoritesMapping.RECIPES, recipe.id))

  const { onFavoriteChange } = useAddOrRemoveFavorite()

  const dispatch = useDispatch()

  const history = useHistory()

  const { handleOnEdit, handleOnDelete } = useManageEatable({
    itemId,
    editRoute: routes.myEatables.myRecipeEdit,
    hideItem
  })

  const callbackEditButton = (): void => handleOnEdit()

  const callbackDeleteButton = (): void => handleOnDelete()

  const handleFavorite = (): void => {
    onFavoriteChange(favoritesRecipe, {
      quantity: 1,
      unit: 'PORTION',
      recipeId: recipe.id
    })
  }

  const [mode, setMode] = useState<Mode>('normal')

  const [toggleChildrenIsOn, setToggleChildrenIsOn] = useState(false)

  const [amount, setAmount] = useState<Amount>({ quantity: 1, unit: 'PORTION' })

  const availableUnits = Utils.availableUnits({ recipe })

  const [date, setDate] = useState<Date>(new Date())

  const [category, setCategory] = useState<MealCategory>(() => Utils.currentMealCategory())

  const handleCategoryChange = (category: SelectItemType): void => {
    setCategory(category as MealCategory)
  }

  const handleSave = async (): Promise<void> => {
    setMode('normal')
    const result = await dispatch(
      postOrPatchJournalEatableRecordAction(
        { recipe, quantity: amount.quantity, unit: amount.unit },
        category,
        date,
        'user-eatables-overview'
      )
    )

    if ('error' in result) {
      return
    }

    dispatch(setJournalDateAction(date))
    history.push('/tagebuch')
  }

  return (
    <CardRecipe
      recipe={recipe}
      onClick={() => {
        setToggleChildrenIsOn(false)
        setMode('normal')
        onClick()
      }}
      cardButtons={[
        <FavoriteCardButtons
          handleSave={handleSave}
          mode={mode}
          setMode={setMode}
          setToggleChildrenIsOn={setToggleChildrenIsOn}
          key={recipe.name}
        />
      ]}
      toggleChildrenIsOn={toggleChildrenIsOn}
      iconButtons={[
        editButton(callbackEditButton),
        favoriteButton(handleFavorite, !!favoritesRecipe),
        deleteButton(callbackDeleteButton)
      ]}
    >
      <div className="standardcard-options">
        <div>
          <FormInputFoodAmount
            label=" "
            amount={amount}
            availableUnits={availableUnits}
            onAmountChange={setAmount}
            onChangeDelay={0}
          />
        </div>

        <div>
          <DatePicker label=" " value={date} onChange={setDate} />
        </div>

        <div>
          <FormInputSelect
            label=" "
            name="mealCategory"
            items={allMealCategories}
            selectedKey={category ? defaultKeyExtractor(category) : undefined}
            labelExtractor={Formatter.formatMealCategory as SelectItemRenderer<SelectItemType, string>}
            keyExtractor={defaultKeyExtractor as SelectItemKeyExtractor<SelectItemType>}
            onChange={handleCategoryChange}
          />
        </div>
      </div>
    </CardRecipe>
  )
}

export default connector(RecipeCardContainer)
